import { useCallback, useContext, useRef, useState } from 'react';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT, DimIdsFilter } from 'components/pages/ProfitPoolOverview/apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useGrowthMarginDriversContext } from '../context/GrowthMarginDriversContext';
import { generateDefaultApplyFilter, getIntersectionOnApplyFilter } from 'utils/helperMethods';
import { FundFlowTreeNode, createChildNodesFromApiData, addChildrenToNode } from '../data/fundFlowData';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import { applyFilterMutation } from 'gqlQueries/filterQueries';
import { useLocation } from 'react-router-dom';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, getScreenFilterDetails, deleteTypeNameFromResponse } from 'utils/helperMethods';

interface BatchNodeItem {
    node: FundFlowTreeNode;
    nextAttributeType: string;
    nextDimension: string;
    nextIsAttribute?: boolean; // Add this
}

interface UseBatchDecompositionReturn {
    processBatch: (nodes: BatchNodeItem[], currentTree: FundFlowTreeNode) => Promise<FundFlowTreeNode | null>;
    isProcessing: boolean;
}

/**
 * Hook for batch processing multiple nodes in parallel
 * Calls apply filter + data API for all nodes simultaneously
 */
const useBatchDecomposition = (): UseBatchDecompositionReturn => {
    const { selectedGrowthMarginDriver } = useGrowthMarginDriversContext();
    const { idForRestOfComponent, timePeriodSelection, singleSelectedTier } = useContext(FilterContextData);
    const [isProcessing, setIsProcessing] = useState(false);
    
    const route = useLocation();
    const { appConfig } = useContext(UserContext);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const { currentScreenFeatureName } = getScreenFilterDetails(route, globalFilterConfig);
    
    // Store refs for latest values
    const idForRestOfComponentRef = useRef(idForRestOfComponent);
    idForRestOfComponentRef.current = idForRestOfComponent;

    // Apollo mutation for apply filter
    const [mutateApplyFilter] = useMutation(applyFilterMutation);

    /**
     * Process apply filter for a single node
     */
    const processApplyFilter = useCallback(
        async (node: FundFlowTreeNode): Promise<DimIdsFilter> => {
            if (!node.attributeType || !node.dimension || !node.attributeExId) {
                return idForRestOfComponentRef.current;
            }

            // Prepare apply filter variables
            const defaultApplyFilterGenerated = generateDefaultApplyFilter(idForRestOfComponentRef.current);
            const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilterGenerated);

            // Add the node's attribute to the appropriate dimension's inclusion array
            const nodeFilterObject = {
                attributeType: node.attributeType,
                attributeExId: node.attributeExId,
                attributeDesc: node.name,
                dimension: node.dimension,
            };

            const dimensionKey = `jsonstring_${node.dimension}`;
            if (tempApplyFilterVariable[dimensionKey]) {
                tempApplyFilterVariable[dimensionKey].inclusion = [nodeFilterObject];
            } else {
                tempApplyFilterVariable[dimensionKey] = {
                    inclusion: [nodeFilterObject],
                };
            }

            // Stringify all dimension variables
            tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1 || {});
            tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2 || {});
            tempApplyFilterVariable.jsonstring_dim3 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim3 || {});
            tempApplyFilterVariable.jsonstring_dim4 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim4 || {});

            try {
                const result = await mutateApplyFilter({
                    variables: {
                        ...tempApplyFilterVariable,
                        featureName: currentScreenFeatureName,
                    },
                });

                if (result.data) {
                    deleteTypeNameFromResponse(result.data);
                    const applyFilterResponse: DimIdsFilter = {
                        dim1: { ids: result.data.dim1?.ids || [] },
                        dim2: { ids: result.data.dim2?.ids || [] },
                        dim3: { ids: result.data.dim3?.ids || [] },
                        dim4: { ids: result.data.dim4?.ids || [] },
                    };
                    return getIntersectionOnApplyFilter(idForRestOfComponentRef.current, applyFilterResponse);
                }
            } catch (error) {
                console.error('Apply filter error for node:', node.name, error);
            }

            return idForRestOfComponentRef.current;
        },
        [mutateApplyFilter, currentScreenFeatureName],
    );

    /**
     * Fetch data for a single node
     */
    const fetchNodeData = useCallback(
        async (
            node: FundFlowTreeNode,
            nextAttributeType: string,
            nextDimension: string,
            nextIsAttribute: boolean, // Add this parameter
            dimIdsFilter: DimIdsFilter,
        ) => {
            try {
                const data = await fetchProfitPoolData({
                    selectedSubFilter: {
                        dataKey: nextAttributeType,
                        dimension: nextDimension,
                        isAttribute: nextIsAttribute ?? true, // Use parameter instead of hardcoded true
                    },
                    limit: PROFIT_POOL_API_LIMIT,
                    offset: 0,
                    kpi: selectedGrowthMarginDriver?.key || 'profit_absolute',
                    tierList: singleSelectedTier,
                    yearAgo: false,
                    groupedByList: ['attribute'],
                    dimIdsFilter: dimIdsFilter,
                    frequency: 'M',
                    timePeriod: timePeriodSelection ? JSON.parse(timePeriodSelection) : undefined,
                    parentAttributeType: node.attributeType,
                    parentDimension: node.dimension,
                });

                return {
                    node,
                    nextAttributeType,
                    nextDimension,
                    data: data?.data || [],
                };
            } catch (error) {
                console.error('Data fetch error for node:', node.name, error);
                return {
                    node,
                    nextAttributeType,
                    nextDimension,
                    data: [],
                };
            }
        },
        [selectedGrowthMarginDriver, singleSelectedTier, timePeriodSelection],
    );

    /**
     * Process a single node: apply filter + fetch data
     */
    const processSingleNode = useCallback(
        async (item: BatchNodeItem) => {
            // Step 1: Get apply filter result
            const dimIdsFilter = await processApplyFilter(item.node);

            // Step 2: Fetch data with the filter
            const result = await fetchNodeData(
                item.node,
                item.nextAttributeType,
                item.nextDimension,
                item.nextIsAttribute ?? true, // Pass nextIsAttribute
                dimIdsFilter,
            );

            return result;
        },
        [processApplyFilter, fetchNodeData],
    );

    /**
     * Process all nodes in parallel and update the tree
     */
    const processBatch = useCallback(
        async (nodes: BatchNodeItem[], currentTree: FundFlowTreeNode): Promise<FundFlowTreeNode | null> => {
            if (nodes.length === 0) return null;

            setIsProcessing(true);

            try {
                // Process all nodes in parallel
                const results = await Promise.all(nodes.map((item) => processSingleNode(item)));

                // Update tree with all results
                let updatedTree = { ...currentTree };

                results.forEach((result) => {
                    if (result.data && result.data.length > 0) {
                        const childNodes = createChildNodesFromApiData(
                            result.data,
                            selectedGrowthMarginDriver?.key || 'profit_absolute',
                            result.nextAttributeType,
                            result.nextDimension,
                            undefined,
                            result.node.id,
                            result.node.attributeExId,
                        );

                        if (childNodes.length > 0) {
                            updatedTree = addChildrenToNode(
                                updatedTree,
                                result.node.id,
                                result.node.name,
                                childNodes,
                            );
                        }
                    }
                });

                return updatedTree;
            } catch (error) {
                console.error('Batch processing error:', error);
                return null;
            } finally {
                setIsProcessing(false);
            }
        },
        [processSingleNode, selectedGrowthMarginDriver],
    );

    return {
        processBatch,
        isProcessing,
    };
};

export default useBatchDecomposition;

